import { useEffect, useState } from "react";
import { Crown, X, RotateCw, Heart } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { AccountOut, TemplateOut, TemplatesPage } from "../types";
import { ScreenHeader } from "./ScreenHeader";
import { openExternalLink } from "../lib/telegram";
import { useDesign } from "../state/designStore";

const SUBSCRIBE_CONTACT_LINK = "https://t.me/hhs9h";

export function PremiumBlockedModal({ onClose }: { onClose: () => void }) {
  const { setOverlayClose } = useDesign();
  useEffect(() => {
    setOverlayClose(() => onClose);
    return () => setOverlayClose(null);
  }, [onClose, setOverlayClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-6" onClick={onClose}>
      <div
        className="glass-card w-full max-w-xs p-5 flex flex-col items-center gap-3 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="w-12 h-12 rounded-full bg-vinyl-sunburst/20 flex items-center justify-center">
          <Crown size={24} className="text-vinyl-sunburst" strokeWidth={2.25} />
        </span>
        <div>
          <span className="block font-bold">هذا الملصق مدفوع (VIP)</span>
          <span className="block text-sm text-tg-hint mt-1">لازم تشترك حتى تكدر تستخدم هذا القالب</span>
        </div>
        <div className="w-full flex flex-col gap-2 mt-1">
          <button
            type="button"
            onClick={() => openExternalLink(SUBSCRIBE_CONTACT_LINK)}
            className="w-full py-2.5 rounded-xl bg-tg-button text-tg-buttonText font-semibold text-sm"
          >
            تواصل مع الدعم للاشتراك
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl glass-pill text-sm font-medium flex items-center justify-center gap-1.5"
          >
            <X size={15} strokeWidth={2.25} />
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

function buildQuery(folder: string | undefined, page: number): string {
  const params = new URLSearchParams();
  if (folder && folder !== "all") params.set("folder", folder);
  params.set("page", String(page));
  return `?${params.toString()}`;
}

export function TemplateGrid({
  onPick,
  onBack,
  folder,
  title,
  favoritesOnly,
}: {
  onPick: (template: TemplateOut) => void;
  onBack: () => void;
  folder?: string; // omitted or "all" = every template, unfiltered
  title?: string;
  favoritesOnly?: boolean; // true = show only the user's favorited templates (no pagination, no folder filter)
}) {
  // templates ACCUMULATES across "load more" clicks - each click
  // appends the next page's items rather than replacing what's
  // already on screen, per the requested behavior (scroll position
  // and everything already loaded stays exactly where it was).
  const [templates, setTemplates] = useState<TemplateOut[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(true); // initial page load for this folder
  const [loadingMore, setLoadingMore] = useState(false); // a "load more" click in flight
  const [error, setError] = useState<string | null>(null);
  const [tier, setTier] = useState<AccountOut["membership"]["tier"] | null>(null);
  const [blockedTemplate, setBlockedTemplate] = useState<TemplateOut | null>(null);
  const [favoritePending, setFavoritePending] = useState<Set<string>>(new Set());

  useEffect(() => {
    api
      .get<AccountOut>("/api/account")
      .then((res) => setTier(res.membership.tier))
      .catch(() => undefined);
  }, []);

  // Switching folders starts over from page 0 - a fresh list, not an
  // append, since it's a different filtered set entirely.
  useEffect(() => {
    let cancelled = false;
    setTemplates([]);
    setPage(0);
    setTotalPages(null);
    setError(null);
    setLoading(true);

    const url = favoritesOnly ? "/api/templates/favorites" : `/api/templates${buildQuery(folder, 0)}`;
    api
      .get<TemplatesPage>(url)
      .then((res) => {
        if (cancelled) return;
        setTemplates(res.items);
        setPage(res.page);
        setTotalPages(res.total_pages);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "تعذر تحميل القوالب");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [folder, favoritesOnly]);

  const hasMore = !favoritesOnly && totalPages !== null && page + 1 < totalPages;

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    setError(null);
    api
      .get<TemplatesPage>(`/api/templates${buildQuery(folder, nextPage)}`)
      .then((res) => {
        // Appends below what's already there - never drops or
        // replaces previously-loaded templates.
        setTemplates((prev) => [...prev, ...res.items]);
        setPage(res.page);
        setTotalPages(res.total_pages);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : "تعذر تحميل المزيد");
      })
      .finally(() => setLoadingMore(false));
  };

  const handlePick = (tpl: TemplateOut) => {
    if (tpl.is_premium && tier === "free") {
      setBlockedTemplate(tpl);
      return;
    }
    onPick(tpl);
  };

  const toggleFavorite = (tpl: TemplateOut, e: React.MouseEvent) => {
    e.stopPropagation();
    if (favoritePending.has(tpl.id)) return;
    setFavoritePending((prev) => new Set(prev).add(tpl.id));

    // Optimistic flip - instant feedback, corrected below if the
    // request actually fails.
    setTemplates((prev) => prev.map((t) => (t.id === tpl.id ? { ...t, is_favorited: !t.is_favorited } : t)));

    api
      .post<{ template_id: string; is_favorited: boolean }>(`/api/templates/${encodeURIComponent(tpl.id)}/favorite`, {})
      .then((res) => {
        setTemplates((prev) => {
          const updated = prev.map((t) => (t.id === tpl.id ? { ...t, is_favorited: res.is_favorited } : t));
          // In the dedicated favorites view, un-favoriting should drop
          // the card from the list entirely rather than just show it
          // unfilled.
          return favoritesOnly ? updated.filter((t) => t.id !== tpl.id || res.is_favorited) : updated;
        });
      })
      .catch(() => {
        // Revert the optimistic flip on failure.
        setTemplates((prev) => prev.map((t) => (t.id === tpl.id ? { ...t, is_favorited: tpl.is_favorited } : t)));
      })
      .finally(() => {
        setFavoritePending((prev) => {
          const next = new Set(prev);
          next.delete(tpl.id);
          return next;
        });
      });
  };

  return (
    <div className="h-full flex flex-col">
      <ScreenHeader title={title ?? (favoritesOnly ? "القوالب المفضلة" : "اختر قالباً")} onBack={onBack} />

      {/* Vertical scroll for the whole screen - a normal page-style
          grid instead of the previous horizontal-swipe strip. Real
          pagination (see PAGE_SIZE server-side) means this only ever
          holds what's actually been loaded so far, not all 150+
          templates rendered/animating at once. */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {error && <p className="text-red-400 text-center text-sm mb-3">{error}</p>}
        {loading && <p className="text-tg-hint text-center text-sm">جاري التحميل...</p>}
        {!loading && templates.length === 0 && !error && (
          <p className="text-tg-hint text-center text-sm">
            {favoritesOnly ? "ما عندك قوالب مفضّلة بعد" : "لا توجد قوالب"}
          </p>
        )}

        {templates.length > 0 && (
          <>
            {/* Responsive 3-column grid, rows grow downward with however
                many templates are loaded - matches the requested
                [sticker][sticker][sticker] x N layout exactly, scrolling
                vertically instead of the old horizontal triplets. */}
            <div className="grid grid-cols-3 gap-3">
              {templates.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => handlePick(tpl)}
                  className="relative glass-card p-2 flex flex-col items-center gap-1.5 text-center"
                >
                  {tpl.is_premium && (
                    <span className="absolute top-1 start-1 z-10 w-5 h-5 rounded-full bg-vinyl-sunburst flex items-center justify-center">
                      <Crown size={11} className="text-black" strokeWidth={2.5} />
                    </span>
                  )}
                  <span
                    role="button"
                    onClick={(e) => toggleFavorite(tpl, e)}
                    className="absolute top-1 end-1 z-10 w-6 h-6 rounded-full bg-black/40 flex items-center justify-center"
                  >
                    <Heart
                      size={12}
                      className={tpl.is_favorited ? "text-red-400" : "text-white"}
                      fill={tpl.is_favorited ? "currentColor" : "none"}
                      strokeWidth={2.25}
                    />
                  </span>
                  {/* Plain image, not a single-frame canvas extraction -
                      that approach didn't hold up in practice. Real
                      pagination (far fewer templates on screen at once
                      than the old all-150-at-a-time list) is what
                      actually keeps this light now. */}
                  <img
                    src={tpl.preview_url}
                    alt={tpl.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full aspect-square object-cover rounded-xl bg-black/20"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                    }}
                  />
                  <span className="text-xs truncate w-full">{tpl.name}</span>
                </button>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-4 mb-2">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl glass-pill text-sm font-medium disabled:opacity-50"
                >
                  {loadingMore ? (
                    <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  ) : (
                    <RotateCw size={14} strokeWidth={2.25} />
                  )}
                  {loadingMore ? "جاري التحميل..." : "جلب المزيد"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {blockedTemplate && <PremiumBlockedModal onClose={() => setBlockedTemplate(null)} />}
    </div>
  );
}
